import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { useUpsertCardMachine } from './useCardMachines';
import type { CardMachine } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  edit?: CardMachine | undefined;
}

function parsePct(s: string): number {
  const cleaned = s.replace(/[^\d,.-]/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

export function CardMachineModal({ open, onOpenChange, edit }: Props) {
  const { accounts } = useFinance();
  const upsert = useUpsertCardMachine();

  const [name, setName] = useState('');
  const [operator, setOperator] = useState('');
  const [mdrDebit, setMdrDebit] = useState('');
  const [mdrCredit, setMdrCredit] = useState('');
  const [mdrInstallments, setMdrInstallments] = useState('');
  const [anticipation, setAnticipation] = useState('');
  const [defaultAccountId, setDefaultAccountId] = useState<string>('none');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setName(edit.name);
      setOperator(edit.operator ?? '');
      setMdrDebit(String(edit.mdr_debit_pct).replace('.', ','));
      setMdrCredit(String(edit.mdr_credit_pct).replace('.', ','));
      setMdrInstallments(String(edit.mdr_installments_pct).replace('.', ','));
      setAnticipation(String(edit.anticipation_rate_monthly_pct).replace('.', ','));
      setDefaultAccountId(edit.default_account_id ?? 'none');
      setNotes(edit.notes ?? '');
      setIsActive(edit.is_active);
    } else {
      setName('');
      setOperator('');
      setMdrDebit('');
      setMdrCredit('');
      setMdrInstallments('');
      setAnticipation('');
      setDefaultAccountId('none');
      setNotes('');
      setIsActive(true);
    }
  }, [open, edit]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = name.trim().length > 0;
  const activeAccounts = accounts.filter((a) => a.isActive);

  const handleSubmit = async () => {
    try {
      await upsert.mutateAsync({
        ...(edit?.id ? { id: edit.id } : {}),
        input: {
          name,
          operator,
          mdrDebitPct: parsePct(mdrDebit),
          mdrCreditPct: parsePct(mdrCredit),
          mdrInstallmentsPct: parsePct(mdrInstallments),
          anticipationRateMonthlyPct: parsePct(anticipation),
          defaultAccountId: defaultAccountId === 'none' ? null : defaultAccountId,
          notes,
          isActive,
        },
      });
      onOpenChange(false);
    } catch {
      // toast já mostrou
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {edit ? 'Editar maquininha' : 'Nova maquininha'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cm-name">Nome *</Label>
              <Input
                id="cm-name"
                placeholder="Ex: Stone Recepção"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cm-operator">Operadora</Label>
              <Input
                id="cm-operator"
                placeholder="Ex: Stone"
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-lg border border-border p-3">
            <div className="mb-2 text-sm font-semibold text-foreground">
              Taxas (% sobre o bruto)
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="mdr-debit" className="text-xs">
                  Débito
                </Label>
                <Input
                  id="mdr-debit"
                  placeholder="1,50"
                  value={mdrDebit}
                  onChange={(e) => setMdrDebit(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mdr-credit" className="text-xs">
                  Crédito 1×
                </Label>
                <Input
                  id="mdr-credit"
                  placeholder="2,50"
                  value={mdrCredit}
                  onChange={(e) => setMdrCredit(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mdr-inst" className="text-xs">
                  Parcelado
                </Label>
                <Input
                  id="mdr-inst"
                  placeholder="3,99"
                  value={mdrInstallments}
                  onChange={(e) => setMdrInstallments(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              <Label htmlFor="anticipation" className="text-xs">
                Antecipação (% ao mês)
              </Label>
              <Input
                id="anticipation"
                placeholder="2,29"
                value={anticipation}
                onChange={(e) => setAnticipation(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Conta padrão de recebimento</Label>
            <Select value={defaultAccountId} onValueChange={setDefaultAccountId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— sem conta padrão —</SelectItem>
                {activeAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-notes">Observações</Label>
            <Input
              id="cm-notes"
              placeholder="Ex: maquininha do consultório central"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded accent-primary"
            />
            Ativa
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || upsert.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {upsert.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Salvando…
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
